import type { ReactNode } from "react";
import { sanitizeDisplayText } from "@/shared/lib/safe-text";

interface SafeTextProps {
  /** The untrusted, user-chosen text (a name). Anything but a string renders as empty. */
  readonly children: string | null | undefined;
  /** Shown when nothing visible is left after sanitizing. */
  readonly fallback?: ReactNode;
  readonly className?: string;
  readonly testId?: string;
}

/**
 * User-chosen text (organization, owner and member names). It is sanitized
 * (no bidi/invisible/control characters) and wrapped in `<bdi>` so a
 * right-to-left name cannot reorder the text around it. Always rendered as a
 * React text node: a name that looks like markup or an address stays text.
 */
export function SafeText({ children, fallback = null, className, testId }: SafeTextProps) {
  const text = sanitizeDisplayText(children);
  if (text === "") return <>{fallback}</>;
  return (
    <bdi data-testid={testId} className={className}>
      {text}
    </bdi>
  );
}
